import { __assign } from "tslib";
import * as React from 'react';
import { Radio, RadioGroup, FormControl, FormLabel, FormControlLabel, makeStyles } from '@material-ui/core';
import { useField } from 'formik';
import { BLayout } from '../BLayout';
import { BError } from './BError';
var useStyles = makeStyles({
    label: {
        width: '100%'
    }
});
/**
 * **Must be a child of BForm**
 *
 * BRadioGroup produces a group of radio buttons that is defined as an array of BRadio<T>.
 *
 *  **Required Props:**
 *  - name
 *      - Must match the name from initialValues
 *
 * **Optional Props:**
 * - radios
 *  - List of radio buttons of type BRadio.
 * - label
 *  - Label for BRadioGroup
 * - required
 *  - Default is false, if true the field will be required
 * - radioGroupProps
 *  - Additional properties that are used in radio groups (uncommon)
 * - labelPlacement
 *  - Label placement relative to the radio button. Default is 'end'
 * - sameButtonColor
 *  - Changes all the buttons to the color that is specified
 * - onChange
 *  - onChange function for when a change is made
 *
 * **Basic Example:**
 * ```jsx
 * <BRadioGroup
 *   name="status"
 *   buttons={choice}
 * />;
 * ```
 *
 * Demos:
 *
 * - [Sample Form](https://material-ui.com/components/text-fields/)
 *
 * API:
 *
 * - [BRadioGroup API](https://gitlab.com/2020-bootcamp/forms/mui-bueno/-/blob/update-docs/docs/tag-references/input/BRadioGroup.md)
 */
export var BRadioGroup = function (props) {
    var _a, _b, _c, _d, _e;
    var name = props.name, onChange = props.onChange, _f = props.radios, radios = _f === void 0 ? [] : _f, _g = props.label, label = _g === void 0 ? name : _g, _h = props.labelPlacement, labelPlacement = _h === void 0 ? 'end' : _h, _j = props.w, w = _j === void 0 ? 3 : _j, _k = props.required, required = _k === void 0 ? false : _k, _l = props.disabled, disabled = _l === void 0 ? false : _l, className = props.className;
    var classes = useStyles();
    // Formik boilerplate ------------------
    var _m = useField({ name: name, validate: function () { return isRequired(); } }), field = _m[0], meta = _m[1], helpers = _m[2];
    var setValue = helpers.setValue;
    var value = field.value;
    // -------------------------------------
    var isRequired = function () {
        if (required && !value) {
            return props.label + " is required";
        }
        else {
            return '';
        }
    };
    var _o = React.useState(function () {
        var index = -1;
        for (var i = 0; i < radios.length; i++) {
            if (radios[i].value.toString() === value) {
                index = i;
            }
        }
        return index;
    }), index = _o[0], setIndex = _o[1];
    var alignLabel = function (labelPlacement) {
        if (labelPlacement == 'start') {
            return 'right';
        }
        else if (labelPlacement == 'top' || labelPlacement == 'bottom') {
            return 'center';
        }
        return 'left';
    };
    var items = radios.map(function (r, i) {
        return (React.createElement(FormControlLabel, { value: i.toString(), control: React.createElement(Radio, __assign({ id: r.value + "-rb", color: props.sameButtonColor ? props.sameButtonColor : (r.color ? r.color : 'default') }, r.radioProps)), label: r.label, labelPlacement: labelPlacement, key: "rb-" + i + "-" + label, disabled: r.disabled || disabled, classes: { label: classes.label }, style: { textAlign: alignLabel(labelPlacement) } }));
    });
    var onChangeWrapped = function (event, val) {
        var i = Number.parseInt(val);
        setIndex(i);
        setValue(radios[i].value.toString());
        if (onChange) {
            onChange(event, radios[i].value);
        }
    };
    return (React.createElement(BLayout, __assign({}, props, { w: w }),
        React.createElement("div", { className: className },
            React.createElement(FormControl, { focused: (_a = props.radioGroupProps) === null || _a === void 0 ? void 0 : _a.focused, margin: (_b = props.radioGroupProps) === null || _b === void 0 ? void 0 : _b.margin, fullWidth: (_c = props.radioGroupProps) === null || _c === void 0 ? void 0 : _c.fullWidth },
                React.createElement(FormLabel, { htmlFor: name, error: required && !((_d = props.radioGroupProps) === null || _d === void 0 ? void 0 : _d.disableRedLabelOnError) && !!meta.error, required: required, disabled: (_e = props.radioGroupProps) === null || _e === void 0 ? void 0 : _e.disableLabel }, label),
                React.createElement(RadioGroup, { id: name, value: index.toString(), onChange: onChangeWrapped }, items)),
            React.createElement(BError, { name: name, id: name + "-err" }))));
};
//# sourceMappingURL=BRadioGroup.js.map